import Button from '@carvana/ds-button';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { LoadingTile } from '@carvana/merch-tile';
import { Fragment, useContext } from 'react';
import CarouselSkeleton from '@/common/Carousel/CarouselSkeleton';
import Carousel from '@/common/helios/Carousel/Carousel';
import TrackWrapper from '~/components/common/TrackWrapper';
import type { AppliedFilters } from '~/contracts/common';
import useCarouselSettings from '~/hooks/ev/useCarouselSettings';
import useGetVehiclesAndAvailability from '~/hooks/ev/useGetVehiclesAndAvailability';
import { EvCarouselSection } from './EvCarousel.styles';
import VehicleTile from './VehicleTile';

type OwnProps = {
  className?: string;
  title: string;
  subtitle: string;
  filterToSet: Partial<AppliedFilters>;
  ctaUrl: string;
};

export default function EvCarousel({
  className = '',
  title,
  subtitle,
  filterToSet,
  ctaUrl
}: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const {
    vehicles: { vehicles, isLoading, isLoaded, error },
    availabilityData,
    isTradeInApplied,
    hasSoftpull
  } = useGetVehiclesAndAvailability(filterToSet);
  const { values, carouselRef } = useCarouselSettings({
    heading: title,
    subHeading: subtitle,
    maxLength: vehicles.length
  });

  if (isLoading)
    return (
      <EvCarouselSection>
        <CarouselSkeleton includeSubHeader Tile={LoadingTile} />
      </EvCarouselSection>
    );

  if (isLoaded && (vehicles?.length < 4 || error)) return null;

  function sendClickEvent(vehicleId: number) {
    trackEvent('Ev Tile', { action: 'clicked', vehicleId });
  }

  return (
    <TrackWrapper event={{ eventName: 'Viewed - Ev Carousel' }}>
      <EvCarouselSection className={className}>
        <Carousel {...values} ref={carouselRef}>
          {vehicles.map(vehicle => {
            const { vehicleId } = vehicle;

            const tileAvailability = availabilityData[vehicleId];

            return (
              <Fragment key={vehicleId}>
                <VehicleTile
                  availabilityInfo={{
                    pickup:
                      tileAvailability?.soonestPickup?.content ?? undefined,
                    delivery:
                      tileAvailability?.soonestDelivery?.content ?? undefined
                  }}
                  vehicle={{
                    ...vehicle,
                    price: vehicle.price
                      ? {
                          ...vehicle.price,
                          totalWithDiscounts: vehicle.price.total
                        }
                      : undefined
                  }}
                  financeInfo={
                    vehicle.price
                      ? {
                          ...vehicle.price,
                          termInMonths: vehicle.price.term
                        }
                      : undefined
                  }
                  hasSoftPull={hasSoftpull}
                  isTradeInApplied={isTradeInApplied}
                  handleTileClick={() => sendClickEvent(vehicleId)}
                />
              </Fragment>
            );
          })}
        </Carousel>
        <div className="flex w-full items-center">
          <a
            href={ctaUrl}
            data-analytics-id="EvCarousel - See More"
            data-testid="EvCarousel - See More"
            className="mx-auto mt-2xl w-full sm:ml-0 sm:mt-5xl sm:w-fit md:ml-auto"
          >
            <Button size="lg" variant="secondary" className="w-full">
              See More
            </Button>
          </a>
        </div>
      </EvCarouselSection>
    </TrackWrapper>
  );
}
